import { useState } from 'react';
import { format } from 'date-fns';
import {
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import type { Conversion } from '@types';

interface HistoryTableProps {
  conversions: Conversion[];
  isLoading: boolean;
  onViewDetails: (conversion: Conversion) => void;
  onDownload: (conversion: Conversion) => void;
  onRetry: (conversion: Conversion) => void;
  onDelete: (conversion: Conversion) => void;
}

type SortField = 'created_at' | 'file_name' | 'status' | 'source_system';
type SortOrder = 'asc' | 'desc';

// Helper function for sort icon - moved outside component to avoid recreation
function getSortIcon(
  field: SortField,
  sortField: SortField,
  sortOrder: SortOrder
) {
  if (sortField !== field) {
    return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
  }
  return sortOrder === 'asc' ? (
    <ChevronUpIcon className="h-4 w-4 text-blue-600" />
  ) : (
    <ChevronDownIcon className="h-4 w-4 text-blue-600" />
  );
}

export function HistoryTable({
  conversions,
  isLoading,
  onViewDetails,
  onDownload,
  onRetry,
  onDelete,
}: HistoryTableProps) {
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedConversions = [...conversions].sort((a, b) => {
    const aRaw = a[sortField];
    const bRaw = b[sortField];

    let aVal: string | number;
    let bVal: string | number;

    if (sortField === 'created_at') {
      aVal = new Date(aRaw as string).getTime();
      bVal = new Date(bRaw as string).getTime();
    } else {
      aVal = String(aRaw).toLowerCase();
      bVal = String(bRaw).toLowerCase();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 ring-green-600/20';
      case 'FAILED':
        return 'bg-red-100 text-red-800 ring-red-600/20';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 ring-yellow-600/20';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 ring-blue-600/20';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
      default:
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-sm text-gray-600">
            Loading conversion history...
          </p>
        </div>
      </div>
    );
  }

  if (conversions.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No conversions found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            No conversion history matches your current filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  <span>Date</span>
                  {getSortIcon('created_at', sortField, sortOrder)}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Conversion ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('file_name')}
              >
                <div className="flex items-center gap-1">
                  <span>Filename</span>
                  {getSortIcon('file_name', sortField, sortOrder)}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('source_system')}
              >
                <div className="flex items-center gap-1">
                  <span>Source System</span>
                  {getSortIcon('source_system', sortField, sortOrder)}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  <span>Status</span>
                  {getSortIcon('status', sortField, sortOrder)}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Records
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedConversions.map(conversion => (
              <tr
                key={conversion.id}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {format(new Date(conversion.created_at), 'MMM d, yyyy')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(conversion.created_at), 'h:mm a')}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2 max-w-[150px]">
                    <span
                      className="font-mono text-xs text-gray-600 truncate"
                      title={String(conversion.id)}
                    >
                      {String(conversion.id)}
                    </span>
                    <button
                      onClick={() => handleCopyId(conversion.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy full ID"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                    {copiedId === conversion.id && (
                      <span className="text-xs text-green-600">Copied!</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div className="truncate" title={conversion.file_name}>
                    {conversion.file_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                    {conversion.source_system}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusBadgeClass(
                      conversion.status
                    )}`}
                  >
                    {conversion.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {conversion.record_count ??
                    conversion.estimated_records ??
                    '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewDetails(conversion)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="View details"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {conversion.status === 'COMPLETED' &&
                      conversion.output_file_id && (
                        <button
                          onClick={() => onDownload(conversion)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Download file"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      )}
                    {conversion.status === 'FAILED' && (
                      <button
                        onClick={() => onRetry(conversion)}
                        className="text-yellow-600 hover:text-yellow-900 transition-colors"
                        title="Retry conversion"
                      >
                        <ArrowPathIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(conversion)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="Delete conversion"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
